import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleCheck as CheckCircle, Chrome as Home, User, Sparkles, Gift, Star, CircleAlert as AlertCircle } from 'lucide-react';
import { toDate } from '../Utils/time';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { PAYPAL_CAPTURE_ORDER_URL, PAYPAL_ORDER_STATUS_URL } from '../../config/api';
import '../PaymentSuccess/PaymentSuccess.css';

interface PaymentDetails {
  order_id: string;
  status: string;
  plan_type: string;
  display_amount: string;
  display_currency: string;
  charged_amount: string;
  charged_currency: string;
  date: string;
  user_id: number;
  subscription_details?: {
    start_date: string;
    expires_at: string;
    plan_type: string;
    status: string;
  };
}

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentProvider, setPaymentProvider] = useState<'paypal' | null>(null);

  useEffect(() => {
    const capturePayment = async () => {
      const storedOrderId = localStorage.getItem('currentOrderId') || searchParams.get('token');

      if (storedOrderId) {
        setPaymentProvider('paypal');
        await handlePayPalPaymentReturn(storedOrderId);
        return;
      }

      setError('No PayPal order was found. Please contact support if you completed a payment.');
      setIsLoading(false);
    };

    const buildPaymentDetails = (
      orderId: string,
      status: string,
      planType: string,
      displayAmount: string,
      displayCurrency: string,
      chargedAmount: string,
      chargedCurrency: string,
      startDate?: string,
      expiresAt?: string,
      paymentStatus?: string,
    ): PaymentDetails => ({
      order_id: orderId,
      status,
      plan_type: planType,
      display_amount: displayAmount,
      display_currency: displayCurrency,
      charged_amount: chargedAmount,
      charged_currency: chargedCurrency,
      date: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      user_id: 0,
      subscription_details: startDate && expiresAt ? {
        start_date: startDate,
        expires_at: expiresAt,
        plan_type: planType,
        status: paymentStatus || 'paid'
      } : undefined
    });

    const handlePayPalPaymentReturn = async (storedOrderId: string) => {
      if (!storedOrderId) {
        setError('No order ID found. Please contact support if you completed a payment.');
        setIsLoading(false);
        return;
      }

      try {
        const headers = { 'Content-Type': 'application/json', Accept: '*/*' };

        // First check if the order is already COMPLETED to avoid re-capturing
        const statusResponse = await authenticatedFetch(PAYPAL_ORDER_STATUS_URL(storedOrderId), {
          method: 'GET',
          headers,
        });

        if (statusResponse.ok) {
          const orderData = await statusResponse.json();

          // If already completed, skip capture and show details directly
          if (orderData.status === 'COMPLETED' && orderData.payment_status === 'paid') {
            const storedPlan = localStorage.getItem('currentOrderPlan') || 'monthly';
            const storedDisplayAmount = localStorage.getItem('currentOrderAmount') || '4.99';
            const storedDisplayCurrency = localStorage.getItem('currentOrderCurrency') || 'GBP';
            const storedChargedAmount = localStorage.getItem('currentChargedAmount') || '4.99';
            const storedChargedCurrency = localStorage.getItem('currentChargedCurrency') || 'GBP';

            setPaymentDetails(buildPaymentDetails(
              orderData.order_id || storedOrderId,
              orderData.status,
              orderData.plan_type || storedPlan.toUpperCase(),
              orderData.amount?.toString() || storedDisplayAmount,
              orderData.currency || storedDisplayCurrency,
              orderData.amount?.toString() || storedChargedAmount,
              orderData.currency || storedChargedCurrency,
              orderData.completed_at || orderData.created_at,
              orderData.expires_at,
              'paid',
            ));

            cleanupStoredData();
            window.history.replaceState({}, document.title, window.location.pathname);
            window.dispatchEvent(new CustomEvent('paymentSuccess'));
            setIsLoading(false);
            return;
          }
        }

        // Order is not yet completed — proceed with capture
        const response = await authenticatedFetch(PAYPAL_CAPTURE_ORDER_URL, {
          method: 'POST',
          headers,
          body: JSON.stringify({ order_id: storedOrderId }),
        });

        const data = await response.json();

        if (response.ok) {
          const storedPlan = localStorage.getItem('currentOrderPlan') || 'monthly';
          const storedDisplayAmount = localStorage.getItem('currentOrderAmount') || '4.99';
          const storedDisplayCurrency = localStorage.getItem('currentOrderCurrency') || 'GBP';
          const storedChargedAmount = localStorage.getItem('currentChargedAmount') || '4.99';
          const storedChargedCurrency = localStorage.getItem('currentChargedCurrency') || 'GBP';

          setPaymentDetails(buildPaymentDetails(
            data.order_id || storedOrderId,
            data.status || 'COMPLETED',
            data.plan_type || storedPlan.toUpperCase(),
            storedDisplayAmount,
            storedDisplayCurrency,
            storedChargedAmount,
            storedChargedCurrency,
            data.starts_at,
            data.expires_at,
            data.payment_status || 'paid',
          ));

          cleanupStoredData();
          window.history.replaceState({}, document.title, window.location.pathname);
          window.dispatchEvent(new CustomEvent('paymentSuccess'));
        } else {
          if (response.status === 401) {
            setError('Authentication failed. Please log in again.');
          } else if (response.status === 409) {
            setError('Your PayPal payment was not approved or has expired. Please try again.');
          } else if (response.status === 502) {
            setError('PayPal could not process the payment. Please try again or contact support if you were charged.');
          } else {
            await checkOrderStatusFallback(storedOrderId);
          }
        }
      } catch {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          await checkOrderStatusFallback(storedOrderId);
          return;
        }

        setError('Network error during payment processing. Please contact support if money was deducted.');
      } finally {
        setIsLoading(false);
      }
    };

    capturePayment();
  // The verifier runs once for the PayPal return URL.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const checkOrderStatusFallback = async (orderId: string) => {
    try {
      const statusResponse = await authenticatedFetch(PAYPAL_ORDER_STATUS_URL(orderId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: '*/*' },
      });

      if (statusResponse.ok) {
        const orderData = await statusResponse.json();

        if (orderData.status === 'COMPLETED' && orderData.payment_status === 'paid') {
          const storedPlan = localStorage.getItem('currentOrderPlan') || 'monthly';
          const storedDisplayCurrency = localStorage.getItem('currentOrderCurrency') || 'GBP';

          setPaymentDetails({
            order_id: orderData.order_id || orderId,
            status: orderData.status,
            plan_type: orderData.plan_type || storedPlan.toUpperCase(),
            display_amount: orderData.amount?.toString() || localStorage.getItem('currentOrderAmount') || '4.99',
            display_currency: orderData.currency || storedDisplayCurrency,
            charged_amount: orderData.amount?.toString() || localStorage.getItem('currentChargedAmount') || '4.99',
            charged_currency: orderData.currency || storedDisplayCurrency,
            date: new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }),
            user_id: 0,
            subscription_details: orderData.expires_at ? {
              start_date: orderData.completed_at || orderData.created_at || '',
              expires_at: orderData.expires_at,
              plan_type: orderData.plan_type || storedPlan.toUpperCase(),
              status: 'paid'
            } : undefined
          });

          cleanupStoredData();
          window.history.replaceState({}, document.title, window.location.pathname);
          window.dispatchEvent(new CustomEvent('paymentSuccess'));
          setIsLoading(false);
          return;
        }
      }
    } catch {
      // Status check also failed - fall through to stored data or error
    }

    setError('We could not confirm your payment status. If you were charged, please contact support with your order ID.');
    setIsLoading(false);
  };

  const cleanupStoredData = () => {
    localStorage.removeItem('currentOrderPlan');
    localStorage.removeItem('currentOrderAmount');
    localStorage.removeItem('currentOrderReference');
    localStorage.removeItem('currentOrderId');
    localStorage.removeItem('currentOrderCurrency');
    localStorage.removeItem('currentChargedAmount');
    localStorage.removeItem('currentChargedCurrency');
    localStorage.removeItem('currentOrderStatus');
    localStorage.removeItem('currentUserId');
    localStorage.removeItem('currentOrderIsSwitch');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewProfile = () => {
    navigate('/profile');
  };

  const handleContactSupport = () => {
    const message = `Hi, I need help with my payment on HomeCookt.%0A%0AOrder ID: ${paymentDetails?.order_id || 'N/A'}%0AStatus: ${paymentDetails?.status || 'N/A'}`;
    window.open(`https://wa.me/447737024736?text=${message}`, '_blank');
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="payment-success-page loading">
        <div className="loading-container">
          <div className="success-spinner">
            <CheckCircle size={60} />
          </div>
          <h2>Processing your payment...</h2>
          <p>Please wait while we confirm your subscription with PayPal</p>
          <div className="loading-steps">
            <div className="step active">Verifying payment</div>
            <div className="step">Activating subscription</div>
            <div className="step">Setting up account</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-success-page error">
        <div className="error-container">
          <div className="error-icon">
            <AlertCircle size={60} />
          </div>
          <h2>Payment Processing Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            {error.includes('Authentication') ? (
              <button onClick={handleLoginRedirect} className="primary-action">
                Go to Login
              </button>
            ) : (
              <button onClick={handleContactSupport} className="primary-action">
                Contact Support
              </button>
            )}
            <button onClick={handleGoHome} className="secondary-action">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="payment-success-page error">
        <div className="error-container">
          <div className="error-icon">
            <AlertCircle size={60} />
          </div>
          <h2>Payment Details Not Found</h2>
          <p>We couldn't retrieve your payment information. Please contact support.</p>
          <div className="error-actions">
            <button onClick={handleContactSupport} className="primary-action">
              Contact Support
            </button>
            <button onClick={handleGoHome} className="secondary-action">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-page">
      <div className="success-container">
        {/* Animated Success Icon */}
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <CheckCircle size={80} />
          </div>
          <div className="success-particles">
            <Sparkles className="particle particle-1" size={20} />
            <Star className="particle particle-2" size={16} />
            <Gift className="particle particle-3" size={18} />
            <Sparkles className="particle particle-4" size={14} />
          </div>
        </div>

        {/* Success Message */}
        <div className="success-content">
          <h1 className="success-title">Payment Successful!</h1>
          <p className="success-subtitle">
            Welcome to HomeCookt! Your {paymentDetails.plan_type.toLowerCase()} subscription is now active and you're ready to start your culinary journey.
            {paymentProvider === 'paypal' && ' Payment processed securely by PayPal.'}
          </p>
        </div>

        {/* Real Payment Details */}
        <div className="order-details-card">
          <h3>Payment Confirmation</h3>
          <div className="order-details-grid">
            <div className="detail-item">
              <span className="label">Plan:</span>
              <span className="value">{paymentDetails.plan_type.charAt(0).toUpperCase() + paymentDetails.plan_type.slice(1)} Subscription</span>
            </div>
            <div className="detail-item">
              <span className="label">Amount Displayed:</span>
              <span className="value">{paymentDetails.display_currency} {paymentDetails.display_amount}</span>
            </div>
            <div className="detail-item">
              <span className="label">Amount Charged:</span>
              <span className="value">{paymentDetails.charged_currency} {paymentDetails.charged_amount}</span>
            </div>
            <div className="detail-item">
              <span className="label">Payment Date:</span>
              <span className="value">{paymentDetails.date}</span>
            </div>
            <div className="detail-item">
              <span className="label">Order ID:</span>
              <span className="value reference">{paymentDetails.order_id}</span>
            </div>
            <div className="detail-item">
              <span className="label">Status:</span>
              <span className="value status-completed">{paymentDetails.status}</span>
            </div>
            {paymentDetails.subscription_details && (
              <div className="detail-item">
                <span className="label">Valid Until:</span>
                <span className="value">{toDate(paymentDetails.subscription_details.expires_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="benefits-section">
          <h3>What's Next?</h3>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon cook">
                <Gift size={24} />
              </div>
              <h4>Start Cooking</h4>
              <p>Share your delicious homemade meals with your community</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon order">
                <Star size={24} />
              </div>
              <h4>Order Food</h4>
              <p>Discover amazing meals from talented home cooks nearby</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon deliver">
                <Sparkles size={24} />
              </div>
              <h4>Earn Money</h4>
              <p>Turn your passion into profit with cooking and delivery</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="success-actions">
          <button onClick={handleGoHome} className="primary-action">
            <Home size={20} />
            Explore HomeCookt
          </button>
          <button onClick={handleViewProfile} className="secondary-action">
            <User size={20} />
            View Profile
          </button>
        </div>

        {/* Footer Message */}
        <div className="success-footer">
          <p>Thank you for joining HomeCookt! We're excited to have you in our community.</p>
          <p className="support-text">
            Need help? Contact us at <a href="mailto:Homecookt.com@gmail.com" aria-label="Email" target="blank">Homecookt.com@gmail.com</a> or
            <a href={`https://wa.me/447737024736?text=Hi, I need help with my payment on HomeCookt. Order ID: ${paymentDetails.order_id}`} target="_blank" rel="noopener noreferrer"> WhatsApp us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
